import React, { useEffect, useRef, useState } from 'react';
import { Send, Paperclip, X, FileText, MessagesSquare, StickyNote } from 'lucide-react';
import { Button } from './ui/button';
import { fmtDateTime } from '../lib/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;

const roleBadge = (role) => ({
  admin: 'bg-tge-navy text-white',
  manager: 'bg-blue-600 text-white',
  counsellor: 'bg-emerald-600 text-white',
  staff: 'bg-emerald-600 text-white',
  student: 'bg-violet-600 text-white',
}[role] || 'bg-slate-500 text-white');

/**
 * Compact single-thread chat used inside the admin's Student Profile modal.
 * Every message sent here is also mirrored into `student_notes` on the
 * backend and shown in the Notes tab.
 *
 * Props:
 *  - studentId: string      (STU-XXXX)
 *  - studentName: string
 *  - studentEmail?: string
 *  - hasPortalAccount: bool (true if student_profile.user_id exists)
 *  - currentUserId: string
 */
export default function StudentProfileChat({
  studentId,
  studentName,
  studentEmail,
  hasPortalAccount,
  currentUserId,
}) {
  const [tab, setTab] = useState('chat'); // 'chat' | 'notes'
  const [thread, setThread] = useState(null);
  const [threadError, setThreadError] = useState('');
  const [messages, setMessages] = useState([]);
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);

  // ---- Open / reuse thread ----
  useEffect(() => {
    if (!studentId) return;
    if (!hasPortalAccount) {
      setThreadError(
        'This student has no portal account yet — ask them to activate their invitation email before chatting.'
      );
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingThread(true);
      setThreadError('');
      try {
        const res = await fetch(
          `${API}/api/admin/students/${studentId}/chat-thread`,
          { method: 'POST', credentials: 'include' }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (!cancelled) setThreadError(err.detail || 'Could not open chat with this student');
          return;
        }
        const t = await res.json();
        if (!cancelled) setThread(t);
      } catch (e) {
        if (!cancelled) setThreadError('Network error opening chat');
      } finally {
        if (!cancelled) setLoadingThread(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, hasPortalAccount]);

  // ---- Poll messages + notes ----
  const fetchMessages = async (silent = false) => {
    if (!thread) return;
    try {
      const res = await fetch(
        `${API}/api/chat/threads/${thread.thread_id}/messages`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (_) {
      /* ignore */
    }
    if (!silent) {
      // Also mark read
      fetch(`${API}/api/chat/threads/${thread.thread_id}/read`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {});
    }
  };

  const fetchNotes = async () => {
    try {
      const res = await fetch(
        `${API}/api/admin/students/${studentId}/notes`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setNotes(data.notes || []);
      }
    } catch (_) {
      /* ignore */
    }
  };

  useEffect(() => {
    if (!thread) return;
    fetchMessages(false);
    fetchNotes();
    const id = setInterval(() => {
      fetchMessages(true);
      fetchNotes();
    }, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.thread_id]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, tab]);

  // ---- Send ----
  const sendMessage = async () => {
    if (sending || !thread) return;
    const text = body.trim();
    if (!text && !attachment) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('body', text);
      if (attachment) fd.append('attachment', attachment);
      const res = await fetch(
        `${API}/api/chat/threads/${thread.thread_id}/messages`,
        { method: 'POST', credentials: 'include', body: fd }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to send');
        return;
      }
      setBody('');
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await fetchMessages(true);
      await fetchNotes();
    } finally {
      setSending(false);
    }
  };

  const downloadAttachment = async (msg) => {
    if (!thread) return;
    const res = await fetch(
      `${API}/api/chat/threads/${thread.thread_id}/attachments/${msg.message_id}`,
      { credentials: 'include' }
    );
    if (!res.ok) return alert('Could not download attachment');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = msg.attachment_name || 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  // ---- Render ----
  if (threadError) {
    return (
      <div
        className="border border-amber-200 bg-amber-50 rounded-xl p-4 text-sm text-amber-900"
        data-testid="student-chat-error"
      >
        {threadError}
      </div>
    );
  }

  return (
    <div
      className="border border-tge-navy/10 rounded-2xl bg-white overflow-hidden"
      data-testid="student-profile-chat"
    >
      {/* Tab bar */}
      <div className="flex border-b border-tge-navy/10 bg-tge-cream-soft/50">
        <button
          onClick={() => setTab('chat')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition ${
            tab === 'chat'
              ? 'text-tge-navy border-b-2 border-tge-navy bg-white'
              : 'text-tge-navy/60 hover:text-tge-navy'
          }`}
          data-testid="student-chat-tab-chat"
        >
          <MessagesSquare size={14} /> Live chat
          {messages.length > 0 && (
            <span className="text-[10px] bg-tge-navy/10 rounded-full px-1.5 py-0.5">
              {messages.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('notes')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition ${
            tab === 'notes'
              ? 'text-tge-navy border-b-2 border-tge-navy bg-white'
              : 'text-tge-navy/60 hover:text-tge-navy'
          }`}
          data-testid="student-chat-tab-notes"
        >
          <StickyNote size={14} /> Notes log
          {notes.length > 0 && (
            <span className="text-[10px] bg-tge-navy/10 rounded-full px-1.5 py-0.5">
              {notes.length}
            </span>
          )}
        </button>
      </div>

      {tab === 'chat' ? (
        <div className="flex flex-col h-[420px]">
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto px-4 py-3 space-y-2 bg-tge-paper/30"
            data-testid="student-chat-messages"
          >
            {loadingThread && (
              <div className="text-center text-xs text-tge-navy/40 py-8">Opening chat…</div>
            )}
            {!loadingThread && messages.length === 0 && (
              <div className="text-center text-sm text-tge-navy/40 py-10">
                No messages yet. Send the first message below —{' '}
                {studentName || 'the student'} will see it in their student portal.
              </div>
            )}
            {messages.map((m) => {
              const mine = m.sender_id === currentUserId;
              return (
                <div key={m.message_id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
                  <div
                    className={`max-w-[78%] rounded-2xl px-4 py-2 shadow-sm ${
                      mine
                        ? 'bg-tge-navy text-white rounded-br-sm'
                        : 'bg-white border border-tge-navy/10 text-tge-navy rounded-bl-sm'
                    }`}
                  >
                    {!mine && (
                      <div className="text-[10px] uppercase tracking-wide opacity-60 mb-0.5 font-medium">
                        {m.sender_name}
                      </div>
                    )}
                    {m.body && (
                      <div className="whitespace-pre-wrap break-words text-sm">{m.body}</div>
                    )}
                    {m.attachment_name && (
                      <button
                        onClick={() => downloadAttachment(m)}
                        className={`mt-2 flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg transition ${
                          mine ? 'bg-white/15 hover:bg-white/25' : 'bg-tge-cream hover:bg-tge-cream/70'
                        }`}
                        data-testid={`student-chat-attachment-${m.message_id}`}
                      >
                        <FileText size={12} />
                        <span className="truncate max-w-[180px]">{m.attachment_name}</span>
                      </button>
                    )}
                    <div className={`text-[10px] mt-1 ${mine ? 'text-white/60' : 'text-tge-navy/40'}`}>
                      {fmtDateTime(m.created_at)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="border-t border-tge-navy/10 bg-white px-3 py-3">
            {attachment && (
              <div className="flex items-center gap-2 text-xs px-3 py-1.5 bg-tge-cream/60 rounded-lg mb-2 w-fit">
                <Paperclip size={12} />
                <span className="truncate max-w-[200px]">{attachment.name}</span>
                <button
                  onClick={() => {
                    setAttachment(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="text-tge-navy/50 hover:text-rose-500"
                >
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-9 h-9 rounded-full hover:bg-tge-cream/60 flex items-center justify-center text-tge-navy/60 hover:text-tge-navy transition shrink-0"
                title="Attach file"
                data-testid="student-chat-attach"
              >
                <Paperclip size={16} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  if (f.size > 10 * 1024 * 1024) {
                    alert('File exceeds 10 MB limit');
                    e.target.value = '';
                    return;
                  }
                  setAttachment(f);
                }}
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                rows={1}
                placeholder={`Message ${studentName || 'student'}…`}
                className="flex-1 resize-none border border-tge-navy/10 rounded-2xl px-4 py-2 text-sm focus:outline-none focus:border-tge-navy/30 max-h-32"
                disabled={!thread || loadingThread}
                data-testid="student-chat-input"
              />
              <Button
                onClick={sendMessage}
                disabled={sending || !thread || (!body.trim() && !attachment)}
                className="bg-tge-navy hover:bg-tge-navy-deep text-white rounded-full h-9 w-9 p-0 shrink-0"
                data-testid="student-chat-send"
              >
                <Send size={14} />
              </Button>
            </div>
            <div className="text-[10px] text-tge-navy/40 mt-2">
              All messages are auto-saved to this student's Notes log for audit.
            </div>
          </div>
        </div>
      ) : (
        <div className="h-[420px] overflow-y-auto px-4 py-3 bg-tge-paper/30" data-testid="student-notes-log">
          {notes.length === 0 ? (
            <div className="text-center text-sm text-tge-navy/40 py-10">
            No notes yet. Any action on this student (assignments, edits, college
              submissions, documents) and every chat message will be logged here automatically.
            </div>
          ) : (
            <ol className="space-y-2">
              {notes.map((n) => {
                const isActivity = n.source === 'activity';
                return (
                <li
                  key={n.note_id}
                  className={`rounded-xl px-3 py-2 text-sm shadow-sm border ${
                    isActivity
                      ? 'bg-amber-50/70 border-amber-200/70'
                      : 'bg-white border-tge-navy/10'
                  }`}
                  data-testid={`student-note-${n.note_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {isActivity ? (
                      <span className="text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 bg-amber-100 text-amber-700">
                        activity
                      </span>
                    ) : (
                      <span
                        className={`text-[10px] uppercase tracking-wide font-semibold rounded-full px-2 py-0.5 ${roleBadge(
                          n.author_role
                        )}`}
                      >
                        {n.author_role || 'user'}
                      </span>
                    )}
                    <span className="text-xs font-medium text-tge-navy truncate">
                      {n.author_name || '—'}
                      {isActivity && n.author_role ? (
                        <span className="text-tge-navy/40 font-normal"> · {n.author_role}</span>
                      ) : null}
                    </span>
                    <span className="text-[10px] text-tge-navy/40 ml-auto">
                      {fmtDateTime(n.created_at)}
                    </span>
                  </div>
                  {n.body && (
                    <div className="whitespace-pre-wrap break-words text-tge-navy/80">{n.body}</div>
                  )}
                  {n.attachment_name && (
                    <div className="text-xs text-tge-navy/60 mt-1 flex items-center gap-1">
                      <FileText size={11} />
                      <span className="truncate">{n.attachment_name}</span>
                    </div>
                  )}
                </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
